import { useState, useCallback } from "react";
import { DeviceInfo } from "../services/vision/types";
import { hashService } from "../services/wasm";
import { timestampService } from "../services/api/timestampService";
// import { evercycleService } from "../services/evercycle/evercycleService";
import { searchService } from "../services/search";
import { toast } from "../components/Toast";
import { JobStatus } from "../types/api";

interface UseRecordSaverProps {
  onError: (message: string) => void;
  onSuccess: () => void;
  resizeImageForStorage: (
    dataUrl: string,
    maxWidth?: number,
    maxSizeKB?: number
  ) => Promise<string>;
  getBase64ImageSizeKB: (base64String: string) => number;
}

interface UseRecordSaverReturn {
  isSaving: boolean;
  hashProgress: number;
  throughput: number;
  saveRecord: (
    imageDataUrl: string,
    deviceInfo: Partial<DeviceInfo> | null
  ) => Promise<void>;
}

export const useRecordSaver = ({
  onError,
  onSuccess,
  resizeImageForStorage,
  getBase64ImageSizeKB,
}: UseRecordSaverProps): UseRecordSaverReturn => {
  const [isSaving, setIsSaving] = useState(false);
  const [hashProgress, setHashProgress] = useState(0);
  const [throughput, setThroughput] = useState(0);

  // FOR EVERCYCLE - Turned OFF
  // const getDeviceTypeId = (type: string) => {
  //   switch (type?.toLocaleUpperCase()) {
  //     case "PHONE":
  //       return 1;
  //     case "LAPTOP":
  //       return 2;
  //     case "TABLET":
  //       return 3;
  //     case "DESKTOP":
  //       return 4;
  //     default:
  //       return 123456; // unknown
  //   }
  // };

  const saveRecord = useCallback(
    async (imageDataUrl: string, deviceInfo: Partial<DeviceInfo> | null) => {
      if (!imageDataUrl) {
        onError("No image captured. Please capture an image first.");
        return;
      }

      // Validate that a title exists
      if (!deviceInfo?.title || deviceInfo.title.trim() === "") {
        onError("A title is required before saving the record.");
        return;
      }

      setIsSaving(true);
      setHashProgress(0);
      setThroughput(0);

      try {
        // Add a small delay to ensure the overlay is visible
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Convert data URL to Blob for hashing
        const response = await fetch(imageDataUrl);
        const blob = await response.blob();
        const imageFile = new File([blob], "device-image.jpg", {
          type: "image/jpeg",
        });

        // Generate hash of the original image file
        const hash = await hashService.hashFile(
          imageFile,
          ({ progress, throughput }) => {
            setHashProgress(progress);
            setThroughput(throughput);
          }
        );

        // Resize and optimize the image for storage
        const resizedImage = await resizeImageForStorage(
          imageDataUrl,
          600,
          100
        );

        const recordTimestamp = new Date().toISOString();

        // Create data object with hash, image, and device info
        const recordData = {
          hash: hash,
          image: resizedImage,
          deviceInfo: deviceInfo || {},
          timestamp: recordTimestamp,
        };

        // Stringify the data object for timestamp creation
        const jsonData = JSON.stringify(recordData);

        // Step 1: Create timestamp with the hash
        const timestampResult = await timestampService.createTimestamp(
          jsonData,
          "SHA256"
        );
        console.log("Timestamp result:", timestampResult);

        // Step 2: Wait for job completion
        let jobStatus: JobStatus | undefined;
        let isComplete = false;
        const maxAttempts = 10;
        let attempts = 0;

        while (!isComplete && attempts < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 2000)); // Wait 2 seconds between checks
          jobStatus = await timestampService.getJobStatus(
            timestampResult.jobId
          );
          console.log("Job status:", jobStatus);
          isComplete = jobStatus.status === "completed";
          attempts++;
        }

        if (!isComplete || !jobStatus) {
          console.error("Job did not complete in the expected time");
          throw new Error("Timestamp creation timed out");
        }

        // Step 3: Verify the timestamp to get the block index
        const certificate = jobStatus.result;
        const verificationResult = await timestampService.verifyTimestamp(
          certificate
        );
        console.log("Verification result:", verificationResult);

        // Step 4: Get all timestamps and filter by block index
        const allTimestamps = await timestampService.getTimestamps();
        const targetTimestamp = allTimestamps.find(
          (ts) => ts.index === verificationResult.blockIndex
        );

        if (!targetTimestamp) {
          console.error(
            "Could not find the timestamp with the specified block index"
          );
          throw new Error("Timestamp not found in blockchain");
        }

        console.log("Complete blockchain record:", targetTimestamp);

        // Create search record with the device metadata and blockchain information
        try {
          const searchMetadata = {
            ...recordData,
            hashAlg: "SHA256",
            recordType: "device",
            blockchainData: {
              index: targetTimestamp.index,
              timestamp: targetTimestamp.timestamp,
              hash: targetTimestamp.hash,
            },
          };

          // Create a search record with the metadata
          const searchResult = await searchService.createSearchRecord(
            searchMetadata
          );
          console.log("Search record created:", searchResult);
        } catch (error) {
          console.error("Error creating search record:", error);
          // Don't fail the entire process if search record creation fails
        }

        // Create asset in Evercycle API
        // try {
        //   const { make, model, serialNumber, type } = deviceInfo || {};

        //   // Prepare the asset data
        //   const assetData = {
        //     asset_type_id: getDeviceTypeId(type ?? ""),
        //     make: make || "Unknown",
        //     model: model || "Unknown",
        //     serial_number: serialNumber || hash.substring(0, 10), // Use part of hash if no serial
        //     asset_reference: hash, // Use the generated hash as a reference
        //     ram: deviceInfo?.ram || "Unknown",
        //     purchase_date: new Date().toISOString().split("T")[0], // Current date in YYYY-MM-DD format
        //     // Add additional fields that might be available from AI extraction
        //     year: new Date().getFullYear(),
        //     asset_user_email: "verae@evercycle.io", // Default user email
        //     asset_status: "Inventory",
        //   };

        //   // Call the Evercycle API to create the asset
        //   const evercycleResult = await evercycleService.createAsset(assetData);

        //   // Check if the asset was created successfully
        //   if (evercycleResult && evercycleResult.status === "success") {
        //     console.log("Evercycle asset created successfully");
        //   } else {
        //     console.warn(
        //       "Evercycle API returned unexpected response:",
        //       evercycleResult
        //     );
        //   }
        // } catch (evercycleError) {
        //   console.error("Error creating asset in Evercycle:", evercycleError);
        //   // We don't want to fail the entire process if Evercycle fails
        //   // Just log the error and continue
        // }

        // Show a success toast notification
        toast({
          title: "Success!",
          description: "Record saved successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });

        // Call the success callback
        onSuccess();
      } catch (err) {
        console.error("Error saving record:", err);
        onError(
          `Failed to save record: ${
            err instanceof Error ? err.message : String(err)
          }`
        );
      } finally {
        // Hide the overlay immediately after processing
        setIsSaving(false);
      }
    },
    [onError, onSuccess, resizeImageForStorage]
  );

  return {
    isSaving,
    hashProgress,
    throughput,
    saveRecord,
  };
};
